import { useEffect, useMemo, useState } from 'react'
import { Users } from 'lucide-react'
import Modal from '../../components/Modal'
import DataTable from '../../components/DataTable'
import StatusBadge from '../../components/StatusBadge'
import PageLoader from '../../components/PageLoader'
import { ErrorState } from '../../components/States'
import { getDataSourceUsers, sourceName } from './settingsService'
import { advancedStrings as S, common as C } from './strings'
import { serverDetail } from '../../lib/i18n'

/** A profile field's own label — the same words the profile settings use for it. */
const fieldLabel = (name) => S.profile.fields.find((field) => field.name === name)?.label ?? name

export default function DataSourceUsersModal({ source, onClose }) {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!source) return
    let active = true
    setLoading(true)
    setError(null)

    getDataSourceUsers(source.id)
      .then((res) => {
        if (!active) return
        if (res.ok) {
          setUsers(res.data ?? [])
        } else {
          setError(serverDetail(res.message) || C.saveFailed)
        }
      })
      .catch((err) => {
        if (active) setError(err.message || C.saveFailed)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [source])

  /* The columns the directory actually returns. "Job title" had a column and no data — the
     server never sends one — so it is the employee number now, which it does. */
  const columns = useMemo(
    () => [
      {
        key: 'username',
        header: fieldLabel('Username'),
        render: (row) => <span className="set-mono" dir="ltr">{row.username}</span>,
      },
      {
        key: 'arabicName',
        header: fieldLabel('ArabicName'),
        render: (row) => <bdi>{row.arabicName}</bdi>,
      },
      {
        key: 'englishName',
        header: fieldLabel('EnglishName'),
        render: (row) => <bdi>{row.englishName}</bdi>,
      },
      {
        key: 'email',
        header: fieldLabel('Email'),
        render: (row) => <span dir="ltr">{row.email}</span>,
      },
      {
        key: 'department',
        header: fieldLabel('DepartmentId'),
        render: (row) => <bdi>{row.department}</bdi>,
      },
      {
        key: 'employeeId',
        header: fieldLabel('EmployeeId'),
        render: (row) => <span dir="ltr">{row.employeeId}</span>,
      },
      {
        key: 'mobile',
        header: fieldLabel('Mobile'),
        value: (row) => row.mobile || row.phone,
        render: (row) => <span dir="ltr">{row.mobile || row.phone}</span>,
      },
      {
        key: 'isResigned',
        header: C.status,
        value: (row) => (row.isResigned ? S.sources.statusResigned : S.sources.statusActive),
        render: (row) => (
          <StatusBadge tone={row.isResigned ? 'danger' : 'success'}>
            {row.isResigned ? S.sources.statusResigned : S.sources.statusActive}
          </StatusBadge>
        ),
      },
    ],
    [],
  )

  const title = `${S.sources.browseUsers} - ${sourceName(source)}`

  return (
    <Modal open={Boolean(source)} onClose={onClose} title={title} icon={Users} size="xl" neutral>
      {loading && <PageLoader />}
      {!loading && error && <ErrorState title={error} />}
      {!loading && !error && (
        <DataTable
          columns={columns}
          rows={users}
          getRowId={(row) => row.username}
          emptyMessage={S.sources.noUsersFound}
        />
      )}
    </Modal>
  )
}
